import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import type { ConversationVolumePanel } from '@/types/analytics'

const PALETTE = ['#7fffe4', '#22d3ee', '#a78bfa', '#f472b6', '#fbbf24', '#34d399']

interface ConversationVolumeCardProps {
  data: ConversationVolumePanel
}

export function ConversationVolumeCard({ data }: ConversationVolumeCardProps) {
  const seriesNameById = Object.fromEntries(data.series.map((s) => [s.id, s.name]))

  const tickFormatter = (iso: string) => {
    const date = new Date(iso)
    if (data.bucket === 'hour') {
      return date.toLocaleTimeString(undefined, { hour: 'numeric' })
    }
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  }

  const tooltipLabelFormatter = (label: unknown) => {
    if (label == null) return ''
    const date = new Date(label as string | number)
    if (Number.isNaN(date.getTime())) return String(label)
    if (data.bucket === 'hour') return date.toLocaleString()
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  }

  return (
    <Card className="p-4">
      <CardHeader className="px-0 pb-3">
        <CardTitle className="text-base">Conversation volume</CardTitle>
        <CardDescription>New conversations, stacked by inbox</CardDescription>
      </CardHeader>
      <CardContent className="px-0 h-72">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data.points} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <XAxis
              dataKey="date"
              tickFormatter={tickFormatter}
              tick={{ fontSize: 11 }}
              stroke="currentColor"
              className="text-muted-foreground"
            />
            <YAxis tick={{ fontSize: 11 }} stroke="currentColor" className="text-muted-foreground" allowDecimals={false} />
            <Tooltip
              labelFormatter={tooltipLabelFormatter}
              formatter={(value, name) => [value as number, seriesNameById[name as string] ?? String(name)]}
              contentStyle={{ background: 'var(--background)', border: '1px solid var(--border)' }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} formatter={(name) => seriesNameById[name as string] ?? String(name)} />
            {data.series.map((series, i) => (
              <Area
                key={series.id}
                type="monotone"
                dataKey={series.id}
                name={series.id}
                stackId="1"
                stroke={PALETTE[i % PALETTE.length]}
                fill={PALETTE[i % PALETTE.length]}
                fillOpacity={0.35}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}
